import { classificationMethodLabel, describeOrigin } from "./mention-origin";
import { Gauge, History } from "lucide-react";
import { useShowsCena } from "@/shared/components/cena-visibility";
import type { Mention } from "@/shared/types/mention";
import { cenaState } from "./mention-cena-state";
// O formatador compartilhado. Aqui era `toFixed(1)`, que escreve "7.4"
// com PONTO — o único lugar da interface fora do padrão pt-BR.
import { formatCena } from "@/shared/utils/format";

// As colunas `p_*` de `PostScoreComponents` — os componentes que somam o
// CENA. Nome cru aparece se surgir um componente novo no back antes de
// alguém traduzir aqui (melhor que esconder o valor).
const componentLabels: Record<string, string> = {
  p_sent: "Favorabilidade",
  p_format: "Formato",
  p_abordagem: "Abordagem",
  p_atributo: "Mensagem-chave",
  p_publi: "Publicador",
  p_canal: "Canal",
  p_veiculo: "Veículo",
  p_porta_voz: "Porta-voz",
  p_titulo: "Citação no título",
  p_format_pesado: "Formato (peso)",
  p_abordagem_pesado: "Abordagem (peso)",
  p_veiculo_pesado: "Veículo (peso)"
};

export function MentionCenaDetail({ mention }: { mention: Mention }) {
  // O portão fica DENTRO do painel de propósito: o drawer e o modal de
  // justificativas o usam, e nenhum dos dois precisa saber da regra.
  const showsCena = useShowsCena();
  const state = cenaState(mention, showsCena);
  const components = Object.entries(mention.cenaComponents);
  // Rede social não tem CENA fechado aqui: o score depende de percentil
  // sobre janela móvel e é calculado no relatório (ReportPostScore).
  //
  // A condição olha a ORIGEM do post, não a ausência de dado. Antes era
  // `escena === null && !components.length`, que nunca dava `true`:
  // publicação de rede JÁ tem os componentes fixos gravados desde o
  // upload (p_sent, p_abordagem, p_canal…) — só faltam os de percentil.
  // O resultado é que este aviso nunca aparecia, e a tela mostrava
  // "Score bruto: —" sem explicar por quê.
  const pending = state.kind === "pending";
  // Menção NEUTRA de imprensa sem peso cadastrado: `escena` NULL sem
  // ser "pendente de relatório". Sem esta distinção o medidor em 0% e o
  // "Score bruto: —" leriam como NOTA ZERO — e zero é outra coisa. As
  // duas condições moram em `cenaState`, dono único.
  const unscored = state.kind === "unscored";
  if (!showsCena) return null;
  return (
    <div className="cena-detail">
      <div>
        <span>ÍNDICE CENA</span>
        <strong>{formatCena(mention.cena)}<small>/10</small></strong>
      </div>
      <div className="cena-gauge"><i style={{ width: `${(mention.cena ?? 0) * 10}%` }}/></div>
      {components.length > 0 && (
        <div className="cena-components">
          {components.map(([name, value]) => (
            <span key={name}>{componentLabels[name] ?? name}<b>{value > 0 ? `+${value}` : value}</b></span>
          ))}
        </div>
      )}
      <p>
        {pending
          ? "Score calculado na amostra — posts de rede dependem do percentil do período."
          : unscored
            ? "Menção neutra — sem nota nesta configuração (o peso do neutro não está cadastrado)."
            : <>Score bruto: <strong>{mention.escena ?? "—"}</strong> · faixa −280 a +280</>}
      </p>
    </div>
  );
}

export function MentionReviewMeta({ mention }: { mention: Mention }) {
  // Método da classificação em vez de "confiança"/"última revisão": o
  // schema não tem coluna pra nenhum dos dois, e `classification_method`
  // é o que de fato responde "de onde veio esse veredito". Quando parte
  // veio da planilha (2026-10-08), a segunda linha diz qual parte.
  const origin = describeOrigin(mention);
  return (
    <div className="review-meta">
      <History size={15}/>
      <span>
        {classificationMethodLabel(mention.classificationMethod)}
        {mention.foundTerms && ` · termos: ${mention.foundTerms.split(";").join(", ")}`}
        {origin && (
          <>
            <br/>
            <span className="review-meta-origin">{origin}</span>
          </>
        )}
      </span>
    </div>
  );
}

/**
 * O CENA do card EXPANDIDO da lista (2026-10-13): o estado em texto no
 * lugar do "—/10" e os componentes como lista, do maior pro menor, com
 * os zeros juntos no fim. Mesmas regras de `MentionCenaDetail` (portão,
 * pendente de relatório, neutra sem nota), outra apresentação — o
 * editor da menção continua com o cartão de sempre.
 */
export function MentionCenaSummary({ mention }: { mention: Mention }) {
  const state = cenaState(mention, useShowsCena());
  if (state.kind === "hidden") return null;
  const pending = state.kind === "pending";
  const unscored = state.kind === "unscored";
  const entries = Object.entries(mention.cenaComponents);
  const scored = entries.filter(([, value]) => value !== 0).sort((a, b) => b[1] - a[1]);
  const zero = entries.filter(([, value]) => value === 0);
  const label = (name: string) => componentLabels[name] ?? name;
  return (
    <section className="cena-summary">
      <header className="cena-summary-head">
        <span className="cena-summary-title"><Gauge size={14} aria-hidden="true"/> Índice CENA</span>
        {pending ? (
          <p className="cena-summary-state">
            <strong>Nota calculada na amostra</strong>
            Posts de rede dependem do percentil do período.
          </p>
        ) : unscored ? (
          <p className="cena-summary-state">
            <strong>Sem nota</strong>
            Menção neutra, e o peso do neutro não está cadastrado.
          </p>
        ) : (
          <div className="cena-summary-score">
            <strong>{formatCena(mention.cena)}</strong>
            <small>/10</small>
            <span>Score bruto {mention.escena ?? "—"} · faixa −280 a +280</span>
          </div>
        )}
      </header>
      {!pending && !unscored && (
        <div className="cena-gauge"><i style={{ width: `${(mention.cena ?? 0) * 10}%` }}/></div>
      )}
      {scored.length > 0 && (
        <ul className="cena-summary-list">
          {scored.map(([name, value]) => (
            <li key={name} className={value < 0 ? "is-negative" : undefined}>
              <span>{label(name)}</span>
              <b>{value > 0 ? `+${value}` : value}</b>
            </li>
          ))}
        </ul>
      )}
      {zero.length > 0 && (
        <p className="cena-summary-zero">
          Sem peso: {zero.map(([name]) => label(name)).join(" · ")}
        </p>
      )}
    </section>
  );
}
